import { useEffect, useRef, useState, type RefObject } from "react";

export interface ContainerSize {
  width: number;
  height: number;
}

export type ContainerAspect = "vertical" | "horizontal" | "square";

interface UseContainerSizeResult<T extends HTMLElement> {
  ref: RefObject<T | null>;
  size: ContainerSize;
}

/**
 * Mesure la taille d'un container via ResizeObserver.
 *
 * Usage typique :
 *   const { ref, size } = useContainerSize<HTMLDivElement>();
 *   return <div ref={ref}>{size.width < 400 ? <Compact /> : <Full />}</div>;
 *
 * Pour la plupart des cas responsive, prefer Tailwind v4 container queries
 * (`@container/panel` + `@xs:`, `@sm:`, etc.) qui resolvent en CSS pur,
 * sans re-render. Ce hook sert quand on a besoin du size en JS (ex: switcher
 * list-view / grid-view selon ratio, virtualisation conditionnelle, etc.).
 *
 * Note React 19 : le ref initial null est correct, le ResizeObserver attache
 * sur le node au mount. StrictMode double-invoke est gere (cleanup propre).
 */
export function useContainerSize<T extends HTMLElement = HTMLDivElement>(): UseContainerSizeResult<T> {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}

/**
 * Helper : derive un aspect ratio d'une taille mesuree.
 *   "vertical" si height > width * 1.3
 *   "horizontal" si width > height * 1.3
 *   "square" sinon
 *
 * Le seuil 1.3 evite les flips constants quand on est proche de 1:1.
 */
export function aspectFromSize(size: ContainerSize): ContainerAspect {
  if (size.width === 0 || size.height === 0) return "square";
  const ratio = size.width / size.height;
  if (ratio < 1 / 1.3) return "vertical";
  if (ratio > 1.3) return "horizontal";
  return "square";
}
